import React, { useEffect, useMemo, useState } from "react";
import { TableRow, TableCell, Stack, Typography } from "@mui/material";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedMonthData,
  setSelectedMonthForecastContentSharedUid,
  setSelectedSite,
} from "../../../../../../features/forecastersSlice";
import MetricsByMonthDialogue from "../../ForeCasterInfo/MetricsByMonth/MetricsByMonth";
import { setUserToken } from "../../../../../../features/userSlice";
import { usePastForecast } from "../../../../../../customHooks/usePastForecast";
import {
  formatPercentageNonRound,
  formatPercentageNonRoundUptoTwo,
} from "../../../../../../utils/helpers";
import { FIELD_NAMES } from "../helpers/GrowthConfig";
import SimpleBackdrop from "../../../../../BackDrop/BackDrop";
import QuarterSummaryCell from "../QuarterSummaryCell";

interface Props {
  showOnlyForecasts: any;
  NonEditableMonths: any;
  RequiredPublishedMonthData: any;
  isMonthPassed: any;
  formatNumber: any;
  MainLoading: any;
  computedLeftOffsets: any;
  computedRightOffsets: any;
  handleCellClick?: (fieldName: string, month: string) => void;
  isHighlighted?: (fieldLabel: string, month: string) => boolean;
  selectedCell?: { fieldName: string; month: string } | null;
  onQuarterTotalsCalculated?: (totals: any[]) => void;
}

export const NonEditableGrowth = ({
  showOnlyForecasts,
  NonEditableMonths,
  RequiredPublishedMonthData,
  isMonthPassed,
  formatNumber,
  MainLoading,
  computedLeftOffsets,
  computedRightOffsets,
  handleCellClick,
  isHighlighted,
  selectedCell,
  onQuarterTotalsCalculated,
}: Props) => {
  const token = useSelector(setUserToken);
  const oauthTokenHeader = token?.payload?.user?.userToken;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const selectedSiteFromRedux = useSelector(
    (state: any) => state.forecasters?.selectedSite,
  );
  const selectedSiteFromURL = searchParams.get("selectedSite");
  const selectedYear = searchParams.get("selectedYear") as any;
  const selectedSite = selectedSiteFromRedux || selectedSiteFromURL;

  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    if (
      location.pathname === "/business_leader/forecaster/forecasterInfo" &&
      newSearchParams.get("page") !== "0"
    ) {
      newSearchParams.set("page", "0");
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.pathname, navigate]);

  // Ensure selectedSite is set in Redux store when component mounts
  useEffect(() => {
    if (selectedSite && !selectedSiteFromRedux) {
      dispatch(setSelectedSite(selectedSite));
    }
  }, [selectedSite, selectedSiteFromRedux, dispatch]);

  const [openMetrics, setOpenMetrics] = useState(false);

  const handleClose = () => {
    setOpenMetrics(false);
  };

  const PassedData =
    RequiredPublishedMonthData === undefined
      ? NonEditableMonths
      : RequiredPublishedMonthData;

  const handleInfoIconClick = (row: any) => {
    dispatch(setSelectedMonthData(row));
    dispatch(setSelectedMonthForecastContentSharedUid(row?.content_shared_uid));
  };

  const { forecastData, loading } = usePastForecast(
    PassedData,
    oauthTokenHeader,
    selectedSite,
  );

  const CleanedHistoryData = useMemo(() => {
    return forecastData.map((data: any) => {
      const cleanedItems = Object.fromEntries(
        Object.entries(data).filter(
          ([key]) =>
            !["input_params", "meta_data", "output_metadata"].includes(key),
        ),
      );
      return cleanedItems;
    });
  }, [forecastData]);

  const transformedData = useMemo(() => {
    return CleanedHistoryData.map((data: any) => {
      // Create a map of metric names to their values
      const metricsMap = (Array.isArray(data?.query_output) ? data.query_output: [])
        ?.reduce((acc: any, metric: any) => {
        const metricKey = metric?.Metric?.toLowerCase();
        acc[metricKey] = {
          forecast: metric?.forecast,
          actual: metric?.Actual,
          deviation: metric?.Deviation,
        };
        return acc;
      }, {});

      // Map the metrics to the desired field names
      return {
        content_shared_uid: data.content_shared_uid,
        month: data.month,
        spend: metricsMap["spend"]?.actual?.toString() || 0,
        clicks: metricsMap["clicks"]?.actual || 0,
        cpc: metricsMap["cpc"]?.actual?.toString() || 0,
        ftd: metricsMap["ftd"]?.actual || 0,
        cvr: metricsMap["cvr"]?.actual?.toString() || 0,
        cac: metricsMap["cac"]?.actual?.toString() || 0,
        profit: metricsMap["profit"]?.actual?.toString() || 0,
        cpa: metricsMap["cpa_income_dollar"]?.actual?.toString() || 0,
        rs: metricsMap["rs_income_dollar"]?.actual?.toString() || 0,
        total_income: metricsMap["total_income"]?.actual?.toString() || 0,
        cpa_pv: metricsMap["cpa_pv"]?.actual?.toString() || 0,
        rs_pv: metricsMap["rs_pv"]?.actual?.toString() || 0,
        pv: metricsMap["pv"]?.actual?.toString() || 0,
        growth_target: metricsMap["growth"]?.actual?.toString() || 0,
        active_users: metricsMap["active_users"]?.actual?.toString() || 0,
        old_active_users:
          metricsMap["old_active_users"]?.actual?.toString() || 0,
        acquired_active_users:
          metricsMap["acquired_active_users"]?.actual?.toString() || 0,
        churn_rate: metricsMap["churn_rate"]?.actual?.toString() || 0,
        ratio_between_acquired_to_ftd:
          metricsMap["ratio_between_acquired_to_ftd"]?.actual?.toString() || 0,
        avg_net_rev_per_active_user:
          metricsMap["avg_net_rev_per_active_user"]?.actual?.toString() || 0,
        net_revenue: metricsMap["net_rev"]?.actual?.toString() || 0,
        avg_rs_deal_p: metricsMap["avg_rs_p"]?.actual?.toString() || 0,
        br_to_ng_active_rs_ratio:
          metricsMap["rs_br_ng"]?.actual?.toString() || 0,
        epc_cpa: metricsMap["epc_cpa"]?.actual?.toString() || 0,
        epc_rs: metricsMap["epc_rs"]?.actual?.toString() || 0,
        epc: metricsMap["epc"]?.actual?.toString() || 0,
        nrpu: metricsMap["nrpu"]?.actual?.toString() || 0,
        displayMonth: data.displayMonth,
      };
    });
  }, [CleanedHistoryData]);

  const sourceData =
    showOnlyForecasts === false &&
    (RequiredPublishedMonthData !== undefined ||
      RequiredPublishedMonthData?.length > 0)
      ? transformedData
      : PassedData;

  useEffect(() => {
    if (!onQuarterTotalsCalculated || !sourceData) return;

    const quarterTotalsArray: any[] = [];

    sourceData.forEach((row: any, rowIndex: number) => {
      const isQuarterEnd = ["March", "June", "September", "December"].includes(
        row.displayMonth,
      );

      if (isQuarterEnd) {
        const quarterMonths = sourceData.slice(
          Math.max(0, rowIndex - 2),
          rowIndex + 1,
        );
        const quarterTotals = {
          ftd: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.ftd) || 0),
            0,
          ),
          profit: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.profit) || 0),
            0,
          ),
          growth: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.growth_target) || 0),
            0,
          ),
          net_revenue: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.net_revenue) || 0),
            0,
          ),
          rs: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.rs) || 0),
            0,
          ),
          cpa: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.cpa) || 0),
            0,
          ),
          total_income: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.total_income) || 0),
            0,
          ),
          spend: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.spend) || 0),
            0,
          ),
          cac: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.cac) || 0),
            0,
          ),
          clicks: quarterMonths?.reduce(
            (sum: number, m: any) => sum + (parseFloat(m.clicks) || 0),
            0,
          ),
        };
        quarterTotalsArray.push(quarterTotals);
      }
    });

    onQuarterTotalsCalculated(quarterTotalsArray);
  }, [sourceData, onQuarterTotalsCalculated, showOnlyForecasts]);

  const getDeviation = (month: string, metricName: string) => {
    // Check if the month has passed — only then include it
    if (!isMonthPassed(month)) return null;

    // Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData.filter((item: any) =>
      isMonthPassed(item.month),
    ).find((item: any) => item.month === month);

    if (!monthData) return null;

    const metricItem = monthData.query_output?.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase(),
    );

    return metricItem?.Deviation ?? null;
  };

  const getActual = (month: string, metricName: string) => {
    // Check if the month has passed — only then include it
    if (!isMonthPassed(month)) return null;

    // Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData.filter((item: any) =>
      isMonthPassed(item.month),
    ).find((item: any) => item.month === month);

    if (!monthData) return null;

    const metricItem = monthData.query_output?.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase(),
    );

    return metricItem?.Actual ?? null;
  };

  const hasRequiredData =
    RequiredPublishedMonthData !== undefined &&
    RequiredPublishedMonthData.length > 0;
  const isLoading =
    RequiredPublishedMonthData !== undefined
      ? loading || MainLoading || (hasRequiredData && forecastData.length === 0)
      : MainLoading;

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <>
      {isLoading ? (
        <SimpleBackdrop />
      ) : (
        <>
          {sourceData?.map((row: any, rowIndex: any) => {
            const isQuarterEnd = [
              "March",
              "June",
              "September",
              "December",
            ].includes(row.displayMonth);

            let quarterTotals: any = null; // Calculate quarter totals if this is a quarter end
            if (isQuarterEnd) {
              const quarterMonths = sourceData.slice(
                Math.max(0, rowIndex - 2),
                rowIndex + 1,
              );
              quarterTotals = {
                ftd: quarterMonths?.reduce(
                  (sum: number, m: any) => sum + (parseFloat(m.ftd) || 0),
                  0,
                ),
                profit: quarterMonths?.reduce(
                  (sum: number, m: any) => sum + (parseFloat(m.profit) || 0),
                  0,
                ),
                growth: quarterMonths?.reduce(
                  (sum: number, m: any) =>
                    sum + (parseFloat(m.growth_target) || 0),
                  0,
                ),
                net_revenue: quarterMonths?.reduce(
                  (sum: number, m: any) =>
                    sum + (parseFloat(m.net_revenue) || 0),
                  0,
                ),
                rs: quarterMonths?.reduce(
                  (sum: number, m: any) => sum + (parseFloat(m.rs) || 0),
                  0,
                ),
                cpa: quarterMonths?.reduce(
                  (sum: number, m: any) => sum + (parseFloat(m.cpa) || 0),
                  0,
                ),
                total_income: quarterMonths?.reduce(
                  (sum: number, m: any) =>
                    sum + (parseFloat(m.total_income) || 0),
                  0,
                ),
                spend: quarterMonths?.reduce(
                  (sum: number, m: any) => sum + (parseFloat(m.spend) || 0),
                  0,
                ),
                cac: quarterMonths?.reduce(
                  (sum: number, m: any) => sum + (parseFloat(m.cac) || 0),
                  0,
                ),
                clicks: quarterMonths?.reduce(
                  (sum: number, m: any) => sum + (parseFloat(m.clicks) || 0),
                  0,
                ),
              };
            }
            return (
              <>
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{
                      backgroundColor: isMonthPassed(row.month)
                        ? "#F5F5F5"
                        : "#FFFFFF",
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                      height: 35,
                    }}
                  >
                    <TableCell
                      align="left"
                      sx={{
                        backgroundColor: "#ffffff",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: computedLeftOffsets[0] ?? 0,
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          <RouterLink
                            to={`MonthlySummery?${(() => {
                              const params = new URLSearchParams(searchParams);
                              params.set("page", "0");
                              params.set(
                                "contentSharedUid",
                                row?.content_shared_uid,
                              );
                              params.set(
                                "month",
                                `${row?.displayMonth} ${selectedYear}`,
                              );
                              params.set("selectedMonth", row?.month);
                              return params.toString();
                            })()}`}
                            onClick={() => handleInfoIconClick(row)}
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              display: "flex",
                              gap: "4px",
                            }}
                          >
                            <Typography variant="body2" color="secondary">
                              ({showOnlyForecasts ? "F" : "A"})
                            </Typography>
                            {row?.displayMonth ? (
                              <Typography
                                variant="body2"
                                color="secondary"
                                component="span"
                                sx={{ textDecoration: "underline" }}
                              >
                                {row.displayMonth}
                              </Typography>
                            ) : (
                              ""
                            )}
                          </RouterLink>
                        ) : (
                          <>
                            <Typography variant="body2" color="secondary">
                              (A)
                            </Typography>
                            {row?.displayMonth ? (
                              <Typography
                                variant="body2"
                                color="secondary"
                                component="span"
                              >
                                {row.displayMonth}
                              </Typography>
                            ) : (
                              ""
                            )}
                          </>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.NRPU, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.NRPU,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: computedLeftOffsets[1],
                        zIndex: 3,
                        cursor: handleCellClick ? "pointer" : "default",

                        border:
                          selectedCell?.fieldName === FIELD_NAMES.NRPU &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.nrpu ? (
                                `${formatNumber(parseFloat(row.nrpu))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    parseFloat(getActual(row.month, "nrpu")),
                                  )}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "nrpu") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.nrpu ? (
                            `${Math.round(Number(row.nrpu))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.FTD, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.FTD,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: computedLeftOffsets[2],
                        zIndex: 3,
                        cursor: handleCellClick ? "pointer" : "default",

                        border:
                          selectedCell?.fieldName === FIELD_NAMES.FTD &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.ftd ? (
                                `${formatNumber(parseFloat(row.ftd))}`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(getActual(row.month, "ftd")) ||
                                    0}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "ftd") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.ftd ? (
                            `${formatNumber(parseFloat(row.ftd))}`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.AVG_NET_REV, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.AVG_NET_REV,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: computedLeftOffsets[3],
                        zIndex: 3,
                        cursor: handleCellClick ? "pointer" : "default",

                        border:
                          selectedCell?.fieldName === FIELD_NAMES.AVG_NET_REV &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.avg_net_rev_per_active_user ? (
                                `${Number(
                                  row.avg_net_rev_per_active_user,
                                ).toFixed(2)}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {`${Number(
                                    getActual(
                                      row.month,
                                      "avg_net_rev_per_active_user",
                                    ),
                                  ).toFixed(2)}$`}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(
                                      row.month,
                                      "avg_net_rev_per_active_user",
                                    ) * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.avg_net_rev_per_active_user ? (
                            `${Number(row.avg_net_rev_per_active_user).toFixed(
                              2,
                            )}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.CPA_PV, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.CPA_PV,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: computedLeftOffsets[4],
                        zIndex: 2,
                        cursor: handleCellClick ? "pointer" : "default",

                        border:
                          selectedCell?.fieldName === FIELD_NAMES.CPA_PV &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.cpa_pv ? (
                                `${formatNumber(parseFloat(row.cpa_pv))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "cpa_pv"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "cpa_pv") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.cpa_pv ? (
                            `${formatNumber(parseFloat(row.cpa_pv))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.CVR, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.CVR,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: computedLeftOffsets[5],
                        zIndex: 2,
                        cursor: handleCellClick ? "pointer" : "default",

                        border:
                          selectedCell?.fieldName === FIELD_NAMES.CVR &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.cvr ? (
                                `${formatPercentageNonRound(row?.cvr)}`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatPercentageNonRound(
                                    getActual(row.month, "CVR"),
                                  )}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "CVR") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.cvr ? (
                            `${formatPercentageNonRound(row?.cvr)}`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.PROFIT, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.PROFIT,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: computedLeftOffsets[6],
                        zIndex: 2,
                        borderRight: "1px solid #e0e0e0 !important",
                        cursor: handleCellClick ? "pointer" : "default",

                        border:
                          selectedCell?.fieldName === FIELD_NAMES.PROFIT &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.profit ? (
                                `${formatNumber(parseFloat(row.profit))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "profit"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "profit") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.profit ? (
                            `${formatNumber(parseFloat(row.profit))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.ACTIVE_USERS, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.ACTIVE_USERS,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        cursor: handleCellClick ? "pointer" : "default",

                        border:
                          selectedCell?.fieldName ===
                            FIELD_NAMES.ACTIVE_USERS &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.active_users ? (
                                `${formatNumber(parseFloat(row.active_users))}`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "active_users"),
                                  ) || 0}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "active_users") *
                                    100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.active_users ? (
                            `${formatNumber(parseFloat(row.active_users))}`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.OLD_USERS, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.OLD_USERS,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.OLD_USERS &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.old_active_users ? (
                                `${formatNumber(
                                  parseFloat(row.old_active_users),
                                )}`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "old_active_users"),
                                  ) || 0}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(
                                      row.month,
                                      "old_active_users",
                                    ) * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.old_active_users ? (
                            `${formatNumber(parseFloat(row.old_active_users))}`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.ACQUIRED_USERS, row.month)
                      }
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.ACQUIRED_USERS,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName ===
                            FIELD_NAMES.ACQUIRED_USERS &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.acquired_active_users ? (
                                `${formatNumber(
                                  parseFloat(row.acquired_active_users),
                                )}`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(
                                      row.month,
                                      "acquired_active_users",
                                    ),
                                  ) || 0}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(
                                      row.month,
                                      "acquired_active_users",
                                    ) * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.acquired_active_users ? (
                            `${formatNumber(
                              parseFloat(row.acquired_active_users),
                            )}`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.GROWTH,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.GROWTH &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.GROWTH, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.growth_target ? (
                                `${formatPercentageNonRoundUptoTwo(
                                  row?.growth_target,
                                )}`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatPercentageNonRoundUptoTwo(
                                    getActual(row.month, "growth"),
                                  )}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "growth") * 100
                                  ).toFixed(2)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.growth_target ? (
                            `${formatPercentageNonRoundUptoTwo(
                              row?.growth_target,
                            )}`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.NET_REVENUE,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.NET_REVENUE &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.NET_REVENUE, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.net_revenue ? (
                                `${formatNumber(parseFloat(row.net_revenue))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "net_rev"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "net_rev") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.net_revenue ? (
                            `${formatNumber(parseFloat(row.net_revenue))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.ESTIMATED_REVENUE_SHARE,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",
                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName ===
                            FIELD_NAMES.ESTIMATED_REVENUE_SHARE &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(
                          FIELD_NAMES.ESTIMATED_REVENUE_SHARE,
                          row.month,
                        )
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.rs ? (
                                `${formatNumber(parseFloat(row.rs))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "rs_income_dollar"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(
                                      row.month,
                                      "rs_income_dollar",
                                    ) * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.rs ? (
                            `${formatNumber(parseFloat(row.rs))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.RS_PV,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.RS_PV &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.RS_PV, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.rs_pv ? (
                                `${formatNumber(parseFloat(row.rs_pv))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "rs_pv"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "rs_pv") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.rs_pv ? (
                            `${formatNumber(parseFloat(row.rs_pv))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.CPA_INCOME,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.CPA_INCOME &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.CPA_INCOME, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.cpa ? (
                                `${formatNumber(parseFloat(row.cpa))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "cpa_income_dollar"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(
                                      row.month,
                                      "cpa_income_dollar",
                                    ) * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.cpa ? (
                            `${formatNumber(parseFloat(row.cpa))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.TOTAL_INCOME,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName ===
                            FIELD_NAMES.TOTAL_INCOME &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.TOTAL_INCOME, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.total_income ? (
                                `${formatNumber(parseFloat(row.total_income))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "total_income"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "total_income") *
                                    100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.total_income ? (
                            `${formatNumber(parseFloat(row.total_income))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.CLICKS,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.CLICKS &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.CLICKS, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.clicks ? (
                                `${formatNumber(parseFloat(row.clicks))}`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "clicks"),
                                  ) || 0}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "clicks") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.clicks ? (
                            `${formatNumber(parseFloat(row.clicks))}`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.EPC_CPA,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.EPC_CPA &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.EPC_CPA, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.epc_cpa ? (
                                `${formatNumber(parseFloat(row.epc_cpa))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "EPC_CPA"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "EPC_CPA") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.epc_cpa ? (
                            `${formatNumber(parseFloat(row.epc_cpa))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.EPC_RS,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.EPC_RS &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.EPC_RS, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.epc_rs ? (
                                `${formatNumber(parseFloat(row.epc_rs))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "EPC_RS"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "EPC_RS") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.epc_rs ? (
                            `${formatNumber(parseFloat(row.epc_rs))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.EPC,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.EPC &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.EPC, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.epc ? (
                                `${formatNumber(parseFloat(row.epc))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(getActual(row.month, "EPC")) ||
                                    0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "EPC") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.epc ? (
                            `${formatNumber(parseFloat(row.epc))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.CPC,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.CPC &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.CPC, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.cpc ? (
                                `${formatNumber(parseFloat(row.cpc))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(getActual(row.month, "CPC")) ||
                                    0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "CPC") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.cpc ? (
                            `${formatNumber(parseFloat(row.cpc))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.CAC,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.CAC &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.CAC, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.cac ? (
                                `${formatNumber(parseFloat(row.cac))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(getActual(row.month, "CAC")) ||
                                    0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "CAC") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.cac ? (
                            `${formatNumber(parseFloat(row.cac))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.SPEND,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : "#F5F5F5",
                        padding: "0px 8px !important",

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.SPEND &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.SPEND, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              row?.spend ? (
                                `${formatNumber(parseFloat(row.spend))}$`
                              ) : (
                                ""
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatNumber(
                                    getActual(row.month, "spend"),
                                  ) || 0}
                                  $
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "spend") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : row?.spend ? (
                            `${formatNumber(parseFloat(row.spend))}$`
                          ) : (
                            ""
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.AVG_RS_P,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : isMonthPassed(row.month)
                            ? "#F5F5F5"
                            : "#FFFFFF",
                        padding: "0px 8px !important",
                        position: "sticky",
                        right: computedRightOffsets[23],
                        zIndex: 2,

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.AVG_RS_P &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.AVG_RS_P, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              formatPercentageNonRound(row?.avg_rs_deal_p)
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatPercentageNonRound(
                                    getActual(row.month, "avg_rs_p"),
                                  )}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "avg_rs_p") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : (
                            formatPercentageNonRound(row?.avg_rs_deal_p)
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.CHURN_RATE,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : isMonthPassed(row.month)
                            ? "#F5F5F5"
                            : "#FFFFFF",
                        padding: "0px 8px !important",
                        position: "sticky",
                        right: computedRightOffsets[24],
                        zIndex: 2,

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.CHURN_RATE &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.CHURN_RATE, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              formatPercentageNonRound(row?.churn_rate)
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatPercentageNonRound(
                                    getActual(row.month, "churn_rate"),
                                  )}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "churn_rate") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : (
                            formatPercentageNonRound(row?.churn_rate)
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.RS_BR_NG,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : isMonthPassed(row.month)
                            ? "#F5F5F5"
                            : "#FFFFFF",
                        padding: "0px 8px !important",
                        position: "sticky",
                        right: computedRightOffsets[25],
                        zIndex: 2,

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName === FIELD_NAMES.RS_BR_NG &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(FIELD_NAMES.RS_BR_NG, row.month)
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              formatPercentageNonRound(
                                row?.br_to_ng_active_rs_ratio,
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatPercentageNonRound(
                                    getActual(row.month, "rs_br_ng"),
                                  )}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(row.month, "rs_br_ng") * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : (
                            formatPercentageNonRound(
                              row?.br_to_ng_active_rs_ratio,
                            )
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isHighlighted?.(
                          FIELD_NAMES.ACQUIRED_TO_FTD,
                          row.month,
                        )
                          ? "#F3E5F5"
                          : isMonthPassed(row.month)
                            ? "#F5F5F5"
                            : "#FFFFFF",
                        padding: "0px 8px !important",
                        position: "sticky",
                        right: computedRightOffsets[26],
                        zIndex: 2,

                        cursor: handleCellClick ? "pointer" : "default",
                        border:
                          selectedCell?.fieldName ===
                            FIELD_NAMES.ACQUIRED_TO_FTD &&
                          selectedCell?.month === row.month
                            ? "2px solid #9C27B0 !important"
                            : "none",
                      }}
                      onClick={() =>
                        handleCellClick?.(
                          FIELD_NAMES.ACQUIRED_TO_FTD,
                          row.month,
                        )
                      }
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography variant="body2">
                          {RequiredPublishedMonthData !== undefined ||
                          RequiredPublishedMonthData?.length > 0 ? (
                            showOnlyForecasts ? (
                              formatPercentageNonRound(
                                row?.ratio_between_acquired_to_ftd,
                              )
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {formatPercentageNonRound(
                                    getActual(
                                      row.month,
                                      "Ratio_between_acquired_to_ftd",
                                    ),
                                  )}
                                </Typography>
                                <Typography variant="body2" color="#00000099">
                                  (
                                  {(
                                    getDeviation(
                                      row.month,
                                      "Ratio_between_acquired_to_ftd",
                                    ) * 100
                                  ).toFixed(1)}
                                  %)
                                </Typography>
                              </Stack>
                            )
                          ) : (
                            formatPercentageNonRound(
                              row?.ratio_between_acquired_to_ftd,
                            )
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {isQuarterEnd && quarterTotals && (
                    <TableRow
                      sx={{
                        backgroundColor: "transparent",
                        "& > *": {
                          borderBottom: "2px solid #E7E7E7 !important",
                        },
                        height: 35,
                      }}
                    >
                      <QuarterSummaryCell
                        value={`Q${Math.ceil((monthNames?.indexOf(row.displayMonth) + 1) / 3)} Total`}
                        bgColor="#F5F5F5"
                        isSticky
                        leftOffset={computedLeftOffsets[0] ?? 0}
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        leftOffset={computedLeftOffsets[1]}
                      />
                      <QuarterSummaryCell
                        value={formatNumber(quarterTotals.ftd)}
                        bgColor="#F5F5F5"
                        isSticky
                        leftOffset={computedLeftOffsets[2]}
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        leftOffset={computedLeftOffsets[3]}
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        leftOffset={computedLeftOffsets[4]}
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        leftOffset={computedLeftOffsets[5]}
                      />
                      <QuarterSummaryCell
                        value={`${formatNumber(quarterTotals.profit)}$`}
                        bgColor="#F5F5F5"
                        isSticky
                        leftOffset={computedLeftOffsets[6]}
                        hasBorder
                      />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                      />
                      <QuarterSummaryCell
                        value={`${formatNumber(quarterTotals.net_revenue)}$`}
                        bgColor="#F5F5F5"
                      />
                      <QuarterSummaryCell
                        value={`${formatNumber(quarterTotals.rs)}$`}
                        bgColor="#F5F5F5"
                      />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell
                        value={`${formatNumber(quarterTotals.cpa)}$`}
                        bgColor="#F5F5F5"
                      />
                      <QuarterSummaryCell
                        value={`${formatNumber(quarterTotals.total_income)}$`}
                        bgColor="#F5F5F5"
                      />
                      <QuarterSummaryCell value={`${formatNumber(quarterTotals.clicks)}`} bgColor="#F5F5F5" />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell bgColor="#F5F5F5" />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                      />
                      <QuarterSummaryCell
                        value={`${formatNumber(quarterTotals.spend)}$`}
                        bgColor="#F5F5F5"
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        rightOffset={computedRightOffsets[23]}
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        rightOffset={computedRightOffsets[24]}
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        rightOffset={computedRightOffsets[25]}
                      />
                      <QuarterSummaryCell
                        bgColor="#F5F5F5"
                        isSticky
                        rightOffset={computedRightOffsets[26]}
                      />
                    </TableRow>
                  )}
                </React.Fragment>
                <MetricsByMonthDialogue
                  open={openMetrics}
                  handleClose={handleClose}
                  selectedSiteFromParent={selectedSite}
                />
              </>
            );
          })}
        </>
      )}
    </>
  );
};
